import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { JobTitleScreeningPreview } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { toCount } from "./titleGate.utils";

interface TitleScreeningGateProps {
	isChecked: boolean;
	onChange: () => void;
	preview?: JobTitleScreeningPreview;
}

/**
 * PREC-3536 Phase-4 title-gate row: the "Screen job titles before enrichment" toggle with the
 * 0.1-credit chip, the explanatory tooltip, and the live "analyzed / new" figures from the
 * screen-preview dry-run.
 */
export const TitleScreeningGate: FunctionComponent<TitleScreeningGateProps> = ({
	isChecked,
	onChange,
	preview,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`csvEnrichment.titleGate.${key}`);

	const analyzed = toCount(preview?.analyzedTitles);
	const newTitles = toCount(preview?.newTitles);

	return (
		<div
			className="mt-4 flex flex-row items-start gap-3 rounded-xl border border-gray-200 bg-white p-4"
			data-test="title-screening-gate"
		>
			<Toggle
				className="mt-0.5"
				isChecked={isChecked}
				name="screenTitles"
				testId="title-screening-toggle"
				onChange={onChange}
			/>
			<div className="min-w-0 flex-1">
				<div className="flex flex-row flex-wrap items-center gap-2">
					<span className="text-sm font-semibold text-text">{ts("toggleLabel")}</span>
					<span className="rounded-md bg-blue-action-200 px-2 py-0.5 text-xs font-semibold text-brand-light">
						{ts("creditChip")}
					</span>
					<Tooltip
						panel
						content={
							<div className="flex flex-col gap-2">
								<span>
									<b>{ts("tooltipTitle")}</b> {ts("tooltipBody")}
								</span>
								{!!preview && (
									<span className="font-semibold">
										{analyzed} {ts("alreadyAnalyzed")} · {newTitles} {ts("newScreened")}
									</span>
								)}
							</div>
						}
						variant="dark"
					>
						<Icon className="h-3.5 w-3.5 cursor-help text-text-meta" icon={faCircleInfo} />
					</Tooltip>
				</div>
				<div className="mt-1 flex flex-col gap-0.5 text-xs text-text-meta">
					{isChecked ? (
						!!preview && (
							<>
								<span data-test="title-screening-analyzed">
									<b className="text-text">{analyzed}</b> {ts("alreadyAnalyzed")}
								</span>
								<span data-test="title-screening-new">
									<b className="text-text">{newTitles}</b> {ts("newScreened")}
								</span>
							</>
						)
					) : (
						<span data-test="title-screening-off-copy">{ts("offCopy")}</span>
					)}
				</div>
			</div>
		</div>
	);
};
